<template>
  <div v-if="hasAdditionalGenericHeadline">
    <component
      :is="headlineProps.component"
      :border="headlineProps.border"
      :color="headlineProps.color"
      class="elevation-1 body-2 pl-6 mb-6"
      :class="headlineTextColorClass"
    >
      <div v-html="headlineText" />
    </component>
  </div>
</template>

<script>
import {
  inject,
  computed,
} from '@vue/composition-api';

import AlertContainer from '@/components/HeadlineContainers/AlertContainer';
import GenericHeadlineContainer from '@/components/HeadlineContainers/GenericHeadlineContainer';
import {
  upperFirst,
} from 'lodash';

export default {
  name: 'results-info-additional-generic-headline',
  components: {
    AlertContainer,
    GenericHeadlineContainer,
  },
  setup () {
    // INJECTS
    const abTastyControllerUI = inject('abTastyControllerUI');
    const productName = inject('productName');

    // COMPUTED
    const selectedHeadline = computed(() => {
      if (productName === 'car') {
        return abTastyControllerUI?.genericHeadlineResultsPage.value || 'Headline0';
      }

      // Dynamically generate the key
      const key = `genericHeadline${upperFirst(productName)}ResultsPage`;

      return abTastyControllerUI?.[key].value || 'Headline0';
    });

    const hasAdditionalGenericHeadline = computed(() => {
      return selectedHeadline.value !== 'Headline0';
    });

    const HEADLINE_TEXTS = {
      smallTextTitle: '<strong>Don’t miss out on these rates!</strong>',
      smallText: 'Prices can change daily - act now to secure your best deal.',
      longTextTitle: '<strong>For your peace of mind</strong>',
      longText: 'ensure that all listed providers are UK Financial Conduct Authority-authorised and covered by the Financial Services Compensation Scheme.',
      fcaProtectionText: '<strong>All listed providers are FCA-authorised and Financial Services Compensation Scheme protected for your peace of mind.</strong>',
      fcaFscsProtectionTextTitle: '<strong>For your peace of mind</strong>',
      fcaFscsProtectionText: 'All listed providers are FCA-authorised and FSCS-covered.',
    };

    const headlineText = computed(() => {
      const {
        smallTextTitle,
        smallText,
        longTextTitle,
        longText,
        fcaProtectionText,
        fcaFscsProtectionTextTitle,
        fcaFscsProtectionText,
      } = HEADLINE_TEXTS;

      const headlines = {
        Headline1: `${smallTextTitle} <strong>${smallText}</strong>`,
        Headline2: `${longTextTitle}<strong>, we ${longText}</strong>`,
        Headline3: ['bike', 'van', 'home'].includes(productName)
          ? fcaProtectionText
          : `${smallTextTitle}<br />${smallText}`,
        Headline4: ['bike', 'van', 'home'].includes(productName)
          ? `${smallTextTitle}<br />${smallText}`
          : `${longTextTitle}<br />We ${longText}`,
        Headline5: ['bike', 'van', 'home'].includes(productName)
          ? `${longTextTitle}<br />We ${longText}`
          : `${smallTextTitle}<br />${smallText}`,
        Headline6: ['bike', 'van', 'home'].includes(productName)
          ? `${fcaFscsProtectionTextTitle}<br />${fcaFscsProtectionText}`
          : `${longTextTitle}<strong>, we ${longText}</strong>`,
        default: `${smallTextTitle}<br />${smallText}`,
      };

      return headlines[selectedHeadline.value] || headlines.default;
    });
    const headlineTextColorClass = computed(() => {
      return ['Headline5', 'Headline6'].includes(selectedHeadline.value) &&
      productName === 'car' ? 'text-blue' : '';
    });
    const headlineProps = computed(() => {
      switch (selectedHeadline.value) {
        case 'Headline1':
        case 'Headline2':
          return {
            component: 'generic-headline-container',
            color: 'orange',
          };
        case 'Headline3':
          if (['bike', 'van', 'home'].includes(productName)) {
            return {
              component: 'generic-headline-container',
              color: 'orange',
            };
          }
          return {
            component: 'alert-container',
            border: 'left',
            color: 'primary',
          };
        case 'Headline4':
          return {
            component: 'alert-container',
            border: 'left',
            color: 'primary',
          };
        case 'Headline5':
          if (['bike', 'van', 'home'].includes(productName)) {
            return {
              component: 'alert-container',
              border: 'left',
              color: 'primary',
            };
          }
          return {
            component: 'generic-headline-container',
            color: 'blue',
          };
        case 'Headline6':
          if (['bike', 'van', 'home'].includes(productName)) {
            return {
              component: 'alert-container',
              border: 'left',
              color: 'primary',
            };
          }
          return {
            component: 'generic-headline-container',
            color: 'blue',
          };
        default:
          return {
            component: 'alert-container',
            border: 'left',
            color: 'primary',
          };
      }
    });

    return {
      // COMPUTED
      hasAdditionalGenericHeadline,
      headlineText,
      headlineTextColorClass,
      headlineProps,
    };
  },
};
</script>
<style lang="scss" scoped>
.text-blue {
  color: $clr-generic-headline-text-blue;
}
</style>
